import { useCallback } from 'react'
import Draggable from 'react-draggable'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import reviews from 'modules/documents/store/collections/reviews'
import Review from 'modules/documents/store/models/Review'

import dots from './dots.svg'
import * as styles from './index.vanilla.css'

interface Props {
  document: Document | undefined
}

const centerElement = (elm: HTMLElement | null | undefined) => {
  if (!elm) return

  const parent = elm.parentElement

  if (!parent) return

  const width = parent.offsetWidth / 2 - elm.offsetWidth / 2
  elm.style.left = `${width}px`
  elm.style.opacity = '1'
}

const ReviewPrompt = ({ document }: Props) => {
  const { access } = useCurrentSession()
  const documentId = document?.id

  const { loading } = useAsyncData(() => {
    if (!documentId) return Promise.resolve()

    return reviews.fetch({
      data: {
        reviewable_type: 'Document',
        reviewable_ids: [documentId],
      },
    })
  }, [documentId])

  const setWidth = useCallback(centerElement, [])

  const review = reviews
    .filterByDocument(document?.id)
    .find((filteredReview) => filteredReview.attributes.access_id === access.id)

  const { performAction: onApprove } = useAsyncAction(() => {
    if (review) {
      review.save({ state: Review.Status.approved })
    }
    return Promise.resolve(null)
  })

  const { performAction: onReject } = useAsyncAction(() => {
    if (review) {
      review.save({
        state: Review.Status.rejected,
      })
    }
    return Promise.resolve(null)
  })

  if (document?.folder?.showOnly) return null

  if (loading) return null

  if (!review) return null

  if (review.isSubmitted) return null

  return (
    <Draggable bounds="parent">
      <div ref={setWidth} className={styles.root}>
        <Box
          className={styles.innerBox}
          padding="s8"
          alignItems="center"
          background="grey000White"
          borderRadius={{
            all: 'abs004',
          }}
        >
          <Box alignItems="center" flexDirection="row" justifyContent="spaceBetween">
            <Box flexDirection="row" alignItems="center" paddingRight="s64">
              <Box paddingRight="s16">
                <img src={dots} />
              </Box>
              <Text.Normal htmlTag="div">
                {i18n.t('reviews.documents.require_approval')}
              </Text.Normal>
            </Box>
            <Box flexDirection="row">
              <Box paddingX="s8">
                <Button onClick={onReject} type="negative">
                  {i18n.t('reviews.documents.reject')}
                </Button>
              </Box>
              <Box paddingX="s8">
                <Button onClick={onApprove} type="positive">
                  {i18n.t('reviews.documents.approve')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </Draggable>
  )
}

export default ReviewPrompt
